import { AxiosResponse } from 'axios';
import { Company } from 'types/company.types';
import _ from 'lodash';
import Logger from '../logger';
import { API_V1 } from './APIs/APIv1';
import { UserResponse } from './user';
import { CompanySearchTypes } from 'features/account/account-management/account-management.types';

export interface CompaniesResponse {
  companies: Company[];
  total: number;
}

const getParams = (
  pageNumber?: number,
  pageSize?: number,
  sortAttribute?: string,
  sortDirection?: string,
  searchParam?: string,
  searchType?: CompanySearchTypes
) => {
  if (pageNumber !== undefined && pageSize) {
    const paramObj: any = {
      limit: pageSize,
      offset: pageNumber * pageSize,
    };
    if (sortAttribute && sortAttribute !== '') {
      paramObj.sort = sortAttribute + ':' + sortDirection;
    }
    if (searchParam) {
      paramObj.q = searchParam;
    }
    if (searchType && searchParam) {
      paramObj.filter = searchType;
    }
    return paramObj;
  }
  return {};
};

export const getCompanies = async (
  pageNumber?: number,
  pageSize?: number,
  sortAttribute?: string,
  sortDirection?: string,
  searchParam?: string,
  searchType?: CompanySearchTypes
): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const response = await API_V1.get<CompaniesResponse>('/tenants/admin/all', {
      params: getParams(pageNumber, pageSize, sortAttribute, sortDirection, searchParam, searchType),
    });
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while getting all companies');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getCompaniesWithCampaignInfo = async (signal?: AbortSignal): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const response = await API_V1.get('/tenants/current/campaign-info', {
      signal,
    });
    return response.data;
  } catch (e) {
    Logger.trace('Error while getting all companies with campaign info');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getCompanyData = async (companyID: string): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const response = await API_V1.get<Company>('/tenants/' + companyID);
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while getting company %s info', companyID);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getOwnCompanyData = async (): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const response = await API_V1.get<Company>('/tenants/current');
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while getting companyinfo');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const changeOwnCompanyData = async (companyData: Partial<Company>): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const postBody: any = {
      name: companyData.name,
      address: {},
    };
    if (companyData.addressLine1) {
      postBody.address.addressLine1 = companyData.addressLine1;
    }
    if (companyData.addressLine2) {
      postBody.address.addressLine2 = companyData.addressLine2;
    }
    if (companyData.postalCode) {
      postBody.address.postalCode = companyData.postalCode;
    }
    if (companyData.city) {
      postBody.address.city = companyData.city;
    }
    if (companyData.country) {
      postBody.address.country = companyData.country;
    }

    if (companyData.companyTaxId) {
      postBody.companyTaxId = companyData.companyTaxId;
    }
    const response = await API_V1.patch('/tenants/current', postBody);
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while changing company info');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const changeCompanyData = async (companyID: string, companyData: Partial<Company>): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const postBody: any = {
      name: companyData.name,
      address: {},
    };
    if (companyData.addressLine1) {
      postBody.address.addressLine1 = companyData.addressLine1;
    }
    if (companyData.addressLine2) {
      postBody.address.addressLine2 = companyData.addressLine2;
    }
    if (companyData.postalCode) {
      postBody.address.postalCode = companyData.postalCode;
    }
    if (companyData.city) {
      postBody.address.city = companyData.city;
    }
    if (companyData.country) {
      postBody.address.country = companyData.country;
    }
    const response = await API_V1.patch('/tenants/' + companyID, postBody);
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while changing company %s info', companyID);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const addCompany = async (companyData: Partial<Company>): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const postBody: any = {
      name: companyData.name,
      address: {},
    };
    if (companyData.addressLine1) {
      postBody.address.addressLine1 = companyData.addressLine1;
    }
    if (companyData.addressLine2) {
      postBody.address.addressLine2 = companyData.addressLine2;
    }
    if (companyData.postalCode) {
      postBody.address.postalCode = companyData.postalCode;
    }
    if (companyData.city) {
      postBody.address.city = companyData.city;
    }
    if (companyData.country) {
      postBody.address.country = companyData.country;
    }
    const response = await API_V1.post('/tenants', postBody);
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while creating new company');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const deleteCompany = async (companyID: string): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const response = await API_V1.delete('/tenants/' + companyID);
    return { data: response.data ?? { success: true } };
  } catch (e) {
    Logger.trace('Error while deleting company %s', companyID);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getUsersByCompany = async (companyID: string) => {
  try {
    const response = await API_V1.get<UserResponse>('/tenants/' + companyID + '/users');
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while getting users of company %s', companyID);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getUsersByCompany2 = async (companyID: string, showLicenses?: boolean, signal?: AbortSignal) => {
  try {
    const response = await API_V1.get(`/tenants/${companyID}/users`, {
      params: { showLicenses: showLicenses || undefined },
      signal,
    });
    return response.data;
  } catch (e) {
    Logger.trace('Error while getting users of company %s', companyID);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getLicensesByCompany = async (companyID: string, page?: number, total?: number, signal?: AbortSignal) => {
  try {
    const params: any = {};
    if (!_.isNil(page) && !_.isNil(total)) {
      params.limit = total;
      params.offset = page * total;
    }
    const response = await API_V1.get(`/tenants/${companyID}/licenses`, {
      params,
      signal,
    });
    return response.data;
  } catch (e) {
    Logger.trace('Error while getting licenses of company %s', companyID);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};
